import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import '../styles/homePage.css';

/**
 * 官网主页（展示落地页）
 * 原样移植自 novel-ai-server/src/seo/homeView.js + layout.js（km9527.cn 的 SSR 主页），
 * 交互（FAQ 手风琴、滚动渐入、移动端菜单）改写为 React 实现。
 */

// 站点信息（与 novel-ai-server/src/seo/siteConfig.js 保持一致）
const SITE_NAME = 'KM智能写作';
const SITE_TAGLINE = 'AI小说一键生成平台';
const SITE_DESCRIPTION = 'KM智能写作是专业的AI小说生成平台，输入创意设定即可一键生成万字长篇小说。支持全自动小说生成，覆盖500章以上长篇创作，是网文作者与内容创作者的AI写作利器。';
const ICP_NUMBER = '桂ICP备2026016503号-2';
const ICP_URL = 'https://beian.miit.gov.cn/';
const BEIAN_NUMBER = '桂公网安备45031202000524号';
const BEIAN_URL = 'https://beian.mps.gov.cn/#/query/webSearch?code=45031202000524';
const BEIAN_ICON = 'https://beian.mps.gov.cn/img/logo01.dd7ff50e.png';

// 友情链接（与 siteConfig.js 保持一致）
const FRIEND_LINKS = [
  {
    name: 'CY-AI音乐助手',
    url: 'https://cy9527.cn/',
    description: 'AI音乐创作工具，64种音乐风格一键生成歌曲，支持批量发布到汽水音乐、抖音音乐平台。',
    tag: 'AI音乐'
  }
];

// 内联SVG图标（无位图依赖，加载快）
const ICONS = {
  novel: (
    <svg viewBox="0 0 24 24" fill="none" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" />
      <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" />
    </svg>
  ),
  guard: (
    <svg viewBox="0 0 24 24" fill="none" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
      <path d="M9 12l2 2 4-4" />
    </svg>
  )
};

// 优势区可视化插图：冲突强度监控曲线（长篇连贯性引擎）
const VizCoherence = () => (
  <div className="viz">
    <div className="viz-head"><span className="viz-dot"></span>冲突强度监控<span className="viz-badge">LIVE</span></div>
    <svg className="viz-svg" viewBox="0 0 320 150" preserveAspectRatio="none" aria-hidden="true">
      <defs>
        <linearGradient id="vizGradA" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="rgba(59,130,246,.28)" />
          <stop offset="100%" stopColor="rgba(59,130,246,0)" />
        </linearGradient>
      </defs>
      <line x1="0" y1="38" x2="320" y2="38" className="viz-grid" />
      <line x1="0" y1="76" x2="320" y2="76" className="viz-grid" />
      <line x1="0" y1="114" x2="320" y2="114" className="viz-grid" />
      <path className="viz-area" d="M0,122 C25,118 42,98 66,102 C90,106 102,74 132,78 C162,82 174,52 206,56 C236,60 246,34 276,30 C296,27 310,20 320,16 L320,150 L0,150 Z" />
      <path className="viz-line" d="M0,122 C25,118 42,98 66,102 C90,106 102,74 132,78 C162,82 174,52 206,56 C236,60 246,34 276,30 C296,27 310,20 320,16" />
      <circle cx="66" cy="102" r="3.5" className="viz-mark" />
      <circle cx="132" cy="78" r="3.5" className="viz-mark" />
      <circle cx="206" cy="56" r="3.5" className="viz-mark" />
      <circle cx="276" cy="30" r="3.5" className="viz-mark" />
    </svg>
    <div className="viz-foot"><span>第1章</span><span className="viz-note">● 伏笔节点</span><span>第500章</span></div>
  </div>
);

// 优势区可视化插图：AI通道调度面板
const VizScheduling = () => (
  <div className="viz">
    <div className="viz-head"><span className="viz-dot"></span>AI通道智能调度</div>
    <div className="viz-body">
      <div className="viz-row">
        <div className="viz-row-label"><b>高性价比模式</b><span className="viz-tag on">活跃</span></div>
        <div className="viz-bar"><i style={{ width: '72%' }}></i></div>
        <div className="viz-row-meta"><span>生成成本</span><b>-87%</b></div>
      </div>
      <div className="viz-row">
        <div className="viz-row-label"><b>高稳定模式</b><span className="viz-tag">待命</span></div>
        <div className="viz-bar"><i style={{ width: '18%' }}></i></div>
        <div className="viz-row-meta"><span>高峰期兜底</span><b>自动切换</b></div>
      </div>
      <div className="viz-pool">
        <div className="viz-row-meta"><span>AI算力池 · 负载均衡</span><b>24 节点在线</b></div>
        <div className="viz-dots"><i className="on"></i><i className="on"></i><i></i><i className="on"></i><i></i><i></i><i className="on"></i><i></i><i></i><i></i><i></i><i></i></div>
      </div>
    </div>
  </div>
);

// 优势区可视化插图：章节控制台
const VizControl = () => (
  <div className="viz">
    <div className="viz-head"><span className="viz-dot"></span>章节控制台<span className="viz-badge">第3卷</span></div>
    <div className="viz-list">
      <div className="viz-ch-row"><span className="viz-ch-num">147</span><span className="viz-ch-title">不速之客</span><span className="viz-ch-st ok">已生成</span></div>
      <div className="viz-ch-row"><span className="viz-ch-num">148</span><span className="viz-ch-title">雨夜追凶</span><span className="viz-ch-st ai">AI重写中</span></div>
      <div className="viz-ch-row"><span className="viz-ch-num">149</span><span className="viz-ch-title">局中局</span><span className="viz-ch-st edit">人工精修</span></div>
      <div className="viz-ch-row"><span className="viz-ch-num">150</span><span className="viz-ch-title">破晓之前</span><span className="viz-ch-st wait">队列中</span></div>
    </div>
    <div className="viz-actions"><span>编辑</span><span>AI重写</span><span>继续生成</span></div>
  </div>
);

const FAQ_ITEMS = [
  {
    q: 'AI一键生成小说是什么原理？',
    a: '您只需输入小说类型、题材、主角设定等创意信息，平台会先生成完整的故事结构（包含人物档案、主线脉络、各章节大纲），再逐章生成正文内容。整个过程支持人工干预调整，兼顾自动化与创作掌控力。'
  },
  {
    q: '能生成多长的小说？',
    a: '平台支持长篇小说创作，单部作品可连续生成500章以上。采用逐章顺序生成机制，每一章都会参考前文内容，保证情节连贯、人物不崩坏。'
  },
  {
    q: '生成的小说版权归谁？',
    a: '您通过平台生成的小说内容版权归您所有，可直接用于网文平台签约发布等商业用途。'
  },
  {
    q: '支持哪些类型的小说？',
    a: '支持玄幻、都市、言情、悬疑、科幻、历史、游戏电竞等全类型网文创作，也可自定义题材混合类型，适配起点、番茄、晋江等主流平台的内容风格。'
  },
  {
    q: '生成的内容质量如何保证？',
    a: '平台内置三层质量守卫：结构连贯性检测（情节重复、角色承接断裂自动识别）、批次间AI审查（衔接与逻辑矛盾检出）、全局终审（伏笔配对、主线完整度校验），问题章节自动定点重生成。'
  },
  {
    q: '断点续写支持吗？',
    a: '支持。所有创作进度实时保存，您可以从任意章节继续创作，也可以对已生成章节进行AI重写与优化，不满意随时调整。'
  }
];

const Home = () => {
  const rootRef = useRef(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [openFaq, setOpenFaq] = useState(null);
  const [loggedIn, setLoggedIn] = useState(false);
  const [articles, setArticles] = useState([]);

  // 文章卡片不展示浏览量，仅展示分类与日期
  const formatDate = (d) => {
    const date = new Date(d);
    return Number.isNaN(date.getTime())
      ? ''
      : `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
  };
  const articleUrl = (slug) => {
    const isDev = process.env.NODE_ENV === 'development' || window.location.hostname === 'localhost';
    const apiServer = process.env.REACT_APP_API_URL
      ? process.env.REACT_APP_API_URL.replace(/\/api\/?$/, '')
      : (isDev ? 'http://localhost:5000' : '');
    return isDev ? `${apiServer}/seosite/articles/${slug}` : `/articles/${slug}`;
  };

  // 已登录用户点击入口直接进入工作台，未登录进入登录窗口
  const entryPath = loggedIn ? '/dashboard' : '/login';

  useEffect(() => {
    const token = localStorage.getItem('authToken');
    const user = localStorage.getItem('user');
    setLoggedIn(!!(token && user));
  }, []);

  // 加载最新文章（后端公开API，仅取已发布文章；本地预览用，生产环境主页由SSR直出）
  useEffect(() => {
    let cancelled = false;
    const isDev = process.env.NODE_ENV === 'development' || window.location.hostname === 'localhost';
    const apiServer = process.env.REACT_APP_API_URL
      ? process.env.REACT_APP_API_URL.replace(/\/api\/?$/, '')
      : (isDev ? 'http://localhost:5000' : '');
    fetch(`${apiServer}/seosite/api/latest-articles`)
      .then((res) => (res.ok ? res.json() : Promise.reject(new Error('bad status'))))
      .then((json) => {
        if (!cancelled && json && Array.isArray(json.data) && json.data.length > 0) {
          setArticles(json.data);
        }
      })
      .catch(() => { /* 无文章或接口不可用时区块整体不显示 */ });
    return () => { cancelled = true; };
  }, []);

  // 滚动渐入动画（IntersectionObserver + scroll/resize 事件兜底，保证任何环境下都触发）
  useEffect(() => {
    const root = rootRef.current;
    if (!root) return undefined;
    const els = Array.from(root.querySelectorAll('.reveal'));
    if (!els.length) return undefined;

    const markVisible = () => {
      const vh = window.innerHeight || document.documentElement.clientHeight;
      els.forEach((el) => {
        if (el.classList.contains('visible')) return;
        const rect = el.getBoundingClientRect();
        if (rect.top < vh * 0.88 && rect.bottom > 0) {
          el.classList.add('visible');
        }
      });
    };

    let io = null;
    if ('IntersectionObserver' in window) {
      io = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('visible');
            io.unobserve(entry.target);
          }
        });
      }, { threshold: 0.12 });
      els.forEach((el) => io.observe(el));
    }

    // 兜底：部分环境（嵌入式预览/后台标签页）IntersectionObserver 不回调，
    // 用 scroll/resize 主动检测视口内的元素
    window.addEventListener('scroll', markVisible, { passive: true });
    window.addEventListener('resize', markVisible, { passive: true });
    // 初次进入视口的元素立即显示
    const timer = setTimeout(markVisible, 60);

    return () => {
      if (io) io.disconnect();
      window.removeEventListener('scroll', markVisible);
      window.removeEventListener('resize', markVisible);
      clearTimeout(timer);
    };
  }, []);

  const toggleFaq = (index) => {
    setOpenFaq((prev) => (prev === index ? null : index));
  };

  return (
    <div className="home-page" ref={rootRef}>
      <div className="tech-bg"></div>

      {/* 顶部导航 */}
      <header className="nav">
        <div className="nav-inner">
          <Link className="logo" to="/" aria-label={`${SITE_NAME}首页`}>
            <span className="logo-mark">KM</span>{SITE_NAME}
          </Link>
          <nav className={menuOpen ? 'nav-links open' : 'nav-links'} aria-label="主导航">
            <a href="#features">核心功能</a>
            <a href="#workflow">创作流程</a>
            <a href="#faq">常见问题</a>
          </nav>
          <Link className="nav-cta" to={entryPath}>开始创作</Link>
          <button
            className="nav-toggle"
            type="button"
            aria-label="打开菜单"
            onClick={() => setMenuOpen((v) => !v)}
          >
            <span></span><span></span><span></span>
          </button>
        </div>
      </header>

      <main>
        {/* Hero */}
        <section className="hero">
          <div className="container">
            <h1><span className="grad">AI小说一键生成</span>平台<br />让创意自动长成万字长篇</h1>
            <p className="hero-sub">输入题材设定，KM智能写作自动构建故事结构、人物档案与章节大纲，逐章生成情节连贯的长篇小说。支持500章以上连续创作，网文作者与内容创作者的AI创作引擎。</p>
            <div className="hero-actions">
              <Link className="btn-primary" to={entryPath}>开始创作</Link>
              <a className="btn-ghost" href="#workflow">了解创作流程</a>
            </div>
            <div className="hero-stats">
              <div className="hero-stat"><b>500+</b><span>章节连续生成</span></div>
              <div className="hero-stat"><b>3层</b><span>质量守卫体系</span></div>
              <div className="hero-stat"><b>100%</b><span>版权归属作者</span></div>
            </div>
          </div>
        </section>

        {/* 核心功能 */}
        <section className="section" id="features">
          <div className="container">
            <div className="section-head reveal">
              <span className="section-tag">CORE FEATURES</span>
              <h2 className="section-title">核心功能</h2>
              <p className="section-sub">从灵感到成稿，覆盖长篇小说创作的完整链路</p>
            </div>
            <div className="cards">
              <div className="card reveal">
                <div className="card-icon">{ICONS.novel}</div>
                <h3>AI小说一键生成</h3>
                <p>输入题材、主角与核心设定，自动生成故事结构、人物关系与章节大纲，逐章产出情节连贯的正文，支持断点续写与定点重写。</p>
              </div>
              <div className="card reveal">
                <div className="card-icon">{ICONS.guard}</div>
                <h3>质量守卫体系</h3>
                <p>三层质量防线贯穿创作全程：情节重复检测、批次AI审查、伏笔与主线终审，问题章节自动定点重生成，长篇不崩不烂尾。</p>
              </div>
            </div>
          </div>
        </section>

        {/* 创作流程 */}
        <section className="section" id="workflow">
          <div className="container">
            <div className="section-head reveal">
              <span className="section-tag">WORKFLOW</span>
              <h2 className="section-title">四步完成一部小说</h2>
              <p className="section-sub">全自动流水线创作，全程可干预可调整</p>
            </div>
            <div className="steps">
              <div className="step reveal">
                <h3>输入创意设定</h3>
                <p>选择小说类型，填写题材、主角设定与核心冲突，或一键使用模板快速开始。</p>
              </div>
              <div className="step reveal">
                <h3>生成故事结构</h3>
                <p>AI构建分卷分章的故事结构：人物档案、主线脉络、冲突强度曲线与伏笔布局。</p>
              </div>
              <div className="step reveal">
                <h3>逐章生成正文</h3>
                <p>按大纲逐章生成正文，每章参考前文保证连贯，质量守卫自动检出并重写问题章节。</p>
              </div>
              <div className="step reveal">
                <h3>导出发布</h3>
                <p>支持章节导出与整本打包，可直接发布到起点、番茄等网文平台。</p>
              </div>
            </div>
          </div>
        </section>

        {/* 产品优势 */}
        <section className="section" id="advantages">
          <div className="container">
            <div className="section-head reveal">
              <span className="section-tag">ADVANTAGES</span>
              <h2 className="section-title">为什么选择KM智能写作</h2>
              <p className="section-sub">为严肃长篇创作而生，而非玩具级demo</p>
            </div>
            <div className="adv reveal">
              <div className="adv-text">
                <h3>长篇连贯性引擎</h3>
                <p>逐章顺序生成机制让每一章都携带前文语境，人物性格、伏笔线索、主线推进全程在线。结构连贯性检测实时监控情节重复与角色承接断裂，问题章节自动定点重生成。</p>
              </div>
              <div className="adv-visual"><VizCoherence /></div>
            </div>
            <div className="adv reveal">
              <div className="adv-text">
                <h3>成本可控的AI智能调度</h3>
                <p>多路AI算力智能调度与负载均衡，生成成本大幅优化。高峰期自动切换稳定通道，批量创作500章任务也能稳定跑完。</p>
              </div>
              <div className="adv-visual tall"><VizScheduling /></div>
            </div>
            <div className="adv reveal">
              <div className="adv-text">
                <h3>创作者完全掌控</h3>
                <p>每一章都可人工编辑、AI重写或局部优化；故事结构支持随时调整后继续生成。您的创意主导一切，AI只是执行引擎。</p>
              </div>
              <div className="adv-visual"><VizControl /></div>
            </div>
          </div>
        </section>

        {/* 适用人群 */}
        <section className="section" id="audiences">
          <div className="container">
            <div className="section-head reveal">
              <span className="section-tag">FOR CREATORS</span>
              <h2 className="section-title">为内容创作者提速</h2>
              <p className="section-sub">无论您是哪种创作者，都能在这里找到杠杆</p>
            </div>
            <div className="audiences">
              <div className="audience reveal">
                <h3>网文作者</h3>
                <p>日更压力大的签约作者，用AI流水线保持稳定更新节奏，把精力留给关键情节的精雕细琢。</p>
              </div>
              <div className="audience reveal">
                <h3>兼职创作者</h3>
                <p>利用碎片时间也能推进长篇创作，AI流水线保持稳定更新节奏，兼职写作同样可以持续产出。</p>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="section" id="faq">
          <div className="container">
            <div className="section-head reveal">
              <span className="section-tag">FAQ</span>
              <h2 className="section-title">常见问题</h2>
              <p className="section-sub">关于AI小说生成的疑问，这里都有答案</p>
            </div>
            <div className="faq-list reveal">
              {FAQ_ITEMS.map((item, index) => (
                <div key={item.q} className={openFaq === index ? 'faq-item open' : 'faq-item'}>
                  <button
                    className="faq-q"
                    type="button"
                    aria-expanded={openFaq === index}
                    onClick={() => toggleFaq(index)}
                  >
                    {item.q}<span className="faq-arrow">▾</span>
                  </button>
                  <div className="faq-a"><div className="faq-a-inner">{item.a}</div></div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* 最新文章（管理后台发布的SEO文章，有已发布文章时显示；卡片不含浏览量） */}
        {articles.length > 0 && (
          <section className="section" id="articles">
            <div className="container">
              <div className="section-head reveal">
                <span className="section-tag">ARTICLES</span>
                <h2 className="section-title">最新文章</h2>
                <p className="section-sub">AI写作技巧与网文创作干货</p>
              </div>
              <div className="articles-grid">
                {articles.map((a) => (
                  <a key={a.slug} className="article-card reveal" href={articleUrl(a.slug)}>
                    <span className="article-cat">{a.category || '创作干货'}</span>
                    <h3 className="article-title">{a.title}</h3>
                    <p className="article-summary">{a.summary}</p>
                    <span className="article-date">{formatDate(a.publishedAt)}</span>
                  </a>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* 友情推荐 */}
        {FRIEND_LINKS.length > 0 && (
          <section className="section" id="friends">
            <div className="container">
              <div className="section-head reveal">
                <span className="section-tag">FRIEND LINKS</span>
                <h2 className="section-title">友情推荐</h2>
                <p className="section-sub">站长同款系列工具，创作路上的好帮手</p>
              </div>
              <div className="friends-grid">
                {FRIEND_LINKS.map((f) => (
                  <div key={f.name} className="friend-card reveal">
                    <div className="friend-card-top">
                      <h3>{f.name}</h3>
                      {f.tag && <span className="friend-tag">{f.tag}</span>}
                    </div>
                    <p>{f.description}</p>
                    <a className="btn-ghost" href={f.url} target="_blank" rel="noopener noreferrer">前往体验 →</a>
                  </div>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* CTA */}
        <section className="section">
          <div className="container">
            <div className="cta-banner reveal">
              <h2>现在开始，让AI替你写完第一部小说</h2>
              <p>注册账号，三分钟生成你的第一章</p>
              <Link className="btn-primary" to="/register">立即开始</Link>
            </div>
          </div>
        </section>
      </main>

      {/* 页脚 */}
      <footer className="footer">
        <div className="container">
          <div className="footer-grid">
            <div className="footer-brand">
              <Link className="logo" to="/">
                <span className="logo-mark">KM</span>{SITE_NAME}
              </Link>
              <p>{SITE_DESCRIPTION}</p>
            </div>
            <div>
              <h4>产品</h4>
              <div className="footer-links">
                <a href="#features">核心功能</a>
                <a href="#workflow">创作流程</a>
                <a href="#advantages">产品优势</a>
                <Link to={entryPath}>进入工作台</Link>
              </div>
            </div>
            <div>
              <h4>资源</h4>
              <div className="footer-links">
                <Link to="/register">注册账号</Link>
                <a href="#faq">常见问题</a>
              </div>
            </div>
            <div>
              <h4>支持</h4>
              <div className="footer-links">
                <a href="#faq">常见问题</a>
                <Link to="/login">登录</Link>
              </div>
            </div>
          </div>
          {FRIEND_LINKS.length > 0 && (
            <div className="footer-friends">
              <span className="footer-friends-label">友情链接</span>
              {FRIEND_LINKS.map((f) => (
                <a key={f.name} href={f.url} target="_blank" rel="noopener noreferrer" title={f.description}>{f.name}</a>
              ))}
            </div>
          )}
          <div className="footer-bottom">
            <span>© {new Date().getFullYear()} {SITE_NAME} {SITE_TAGLINE}</span>
            <div className="footer-filing">
              <a href={ICP_URL} target="_blank" rel="nofollow noopener noreferrer">{ICP_NUMBER}</a>
              <a href={BEIAN_URL} target="_blank" rel="noreferrer" className="beian-link">
                <img src={BEIAN_ICON} alt="公安备案" className="beian-icon" />
                {BEIAN_NUMBER}
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Home;
